// ============================================
// Home Module - Content Loading & Rendering (ES6)
// ============================================

import { useHomeStore } from '../../stores/homeStore.js';
import { escapeHtml, formatPlayCount } from '../utils.js';
import { recordImpression } from '../analytics/stats-buffer.js';

// ============================================
// 노출 추적 (Impression Tracking)
// ============================================

const observedCards = new Set();
let impressionObserver = null;

function getImpressionObserver() {
  if (impressionObserver) return impressionObserver;

  impressionObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const card = entry.target;
        const contentId = card.dataset.contentId;
        if (contentId && !observedCards.has(contentId)) {
          observedCards.add(contentId);
          recordImpression(contentId);
        }
      }
    });
  }, {
    root: null,
    rootMargin: '0px',
    threshold: 0.5 // 50% 이상 보여야 노출로 인정
  });

  return impressionObserver;
}

// ============================================
// 홈 카테고리 전환
// ============================================

export function switchHomeCategory(category) {
  const store = useHomeStore.getState();
  if (store.homeCategoryFilter === category) return;
  store.setHomeCategoryFilter(category);

  document.querySelectorAll('.home-tab-btn').forEach(tab => {
    tab.classList.remove('active');
    if (tab.dataset.category === category) {
      tab.classList.add('active');
    }
  });

  // 랭킹 기간 드롭다운 표시/숨김
  const periodSelect = document.getElementById('rankingPeriodSelect');
  if (periodSelect) {
    periodSelect.style.display = category === 'ranking' ? 'inline-block' : 'none';
  }

  // 해당 타입의 캐시가 비어있으면 로드
  const cache = store.publishedContentCache;
  const hasMore = store.hasMoreContentCache;
  if (cache[category].length === 0 && hasMore[category]) {
    loadHomeContent(0, false);
  } else {
    renderHomeGrid();
  }
}

/**
 * 랭킹 기간 변경 핸들러
 */
export function changeRankingPeriod(period) {
  const store = useHomeStore.getState();
  if (store.rankingPeriod === period) return;

  store.setRankingPeriod(period);
  store.resetCacheForType('ranking');
  loadHomeContent(0, false);
}

// ============================================
// 홈 콘텐츠 로드
// ============================================

export async function loadHomeContent(retryCount = 0, loadMore = false) {
  const gridEl = document.getElementById('homeContentGrid');
  if (!gridEl) {
    console.warn('Home content grid not found');
    return;
  }

  const maxRetries = 3;
  const pageSize = 10;
  const store = useHomeStore.getState();
  const currentType = store.homeCategoryFilter;

  // Firestore 인스턴스 초기화
  const db = store.getDb();

  // 초기 로딩 시 해당 타입 캐시 리셋
  if (!loadMore) {
    store.resetCacheForType(currentType);
    gridEl.innerHTML = '<div class="loading-state"><div class="spinner"></div></div>';
  }

  // 이미 로딩 중이거나 더 이상 콘텐츠가 없으면 리턴
  if (store.isLoadingMore || (!loadMore && retryCount > 0)) return;
  if (loadMore && !store.hasMoreContentCache[currentType]) return;

  store.setIsLoadingMore(true);

  try {
    // Firestore 쿼리 - 타입별로 필터링
    let query;
    if (currentType === 'ranking') {
      // 랭킹: 기간별 점수순 (daily/weekly/allTime)
      const period = store.rankingPeriod || 'daily';
      const scoreField = period === 'daily' ? 'scoreDaily'
                       : period === 'weekly' ? 'scoreWeekly'
                       : 'scoreAllTime';
      query = db.collection('published')
        .where('status', '==', 'public')
        .orderBy(scoreField, 'desc')
        .limit(pageSize + 1);
    } else {
      // 캐릭터: chatCount 순 정렬
      query = db.collection('published')
        .where('status', '==', 'public')
        .where('type', '==', 'character')
        .orderBy('chatCount', 'desc')
        .limit(pageSize + 1);
    }

    // 커서 기반 페이지네이션
    const lastDoc = useHomeStore.getState().lastPublishedDocCache[currentType];
    if (loadMore && lastDoc) {
      if (currentType === 'ranking') {
        const period = store.rankingPeriod || 'daily';
        const scoreField = period === 'daily' ? 'scoreDaily'
                         : period === 'weekly' ? 'scoreWeekly'
                         : 'scoreAllTime';
        query = db.collection('published')
          .where('status', '==', 'public')
          .orderBy(scoreField, 'desc')
          .startAfter(lastDoc)
          .limit(pageSize + 1);
      } else {
        query = db.collection('published')
          .where('status', '==', 'public')
          .where('type', '==', 'character')
          .orderBy('chatCount', 'desc')
          .startAfter(lastDoc)
          .limit(pageSize + 1);
      }
    }

    const snapshot = await query.get();
    const docs = snapshot.docs;

    // 다음 페이지 여부 확인
    const hasMoreItems = docs.length > pageSize;
    store.setHasMoreForType(currentType, hasMoreItems);
    const itemDocs = hasMoreItems ? docs.slice(0, pageSize) : docs;

    // 마지막 문서 저장 (페이지네이션용)
    if (itemDocs.length > 0) {
      store.setLastDocForType(currentType, itemDocs[itemDocs.length - 1]);
    }

    // 데이터 변환 (메타데이터만 - 상세 데이터는 상세 모달에서 로드)
    const newItems = itemDocs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title || '제목 없음',
        description: data.description || data.shortDesc || '',
        shortDesc: data.shortDesc || '',
        type: data.type || 'character',
        tags: data.tags || [],
        imageUrl: data.imageUrl || null,
        thumbUrl: data.thumbUrl || null,
        chatCount: data.chatCount || 0,
        likeCount: data.likeCount || 0,
        authorNickname: data.authorNickname || data.authorName || '익명',
        authorName: data.authorName || data.authorNickname || '익명',
        createdAt: data.createdAt?.toDate?.()?.getTime() || 0,
        safetyFilter: data.safetyFilter === false ? false : true,
        authorId: data.authorId || null,
        isOfficialCreator: data.isOfficialCreator === true
      };
    });

    // 해당 타입 캐시에 추가
    store.appendCacheForType(currentType, newItems);

    // append 시 새로 추가된 아이템만 전달
    renderHomeGrid(loadMore, loadMore ? newItems : null);
  } catch (e) {
    console.error(`Failed to load home content (attempt ${retryCount + 1}):`, e);
    if (!loadMore && retryCount < maxRetries) {
      store.setIsLoadingMore(false);
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
      return loadHomeContent(retryCount + 1, false);
    }
    if (!loadMore) {
      gridEl.innerHTML = '<div class="empty-state-section">콘텐츠 로딩 실패. <span style="color: var(--accent); cursor: pointer;" data-action="retry">다시 시도</span></div>';
      gridEl.querySelector('[data-action="retry"]')?.addEventListener('click', () => loadHomeContent());
    } else {
      // loadMore 실패 시 스피너 제거하고 재시도 가능하도록
      const loadingEl = gridEl.querySelector('.load-more-spinner');
      if (loadingEl) loadingEl.remove();
      store.setHasMoreForType(currentType, true); // 재시도 가능하도록 유지
    }
  } finally {
    useHomeStore.getState().setIsLoadingMore(false);
  }
}

// ============================================
// 무한 스크롤
// ============================================

export function setupInfiniteScroll() {
  const homeGrid = document.getElementById('homeContentGrid');
  if (!homeGrid) return;

  homeGrid.addEventListener('scroll', () => {
    const store = useHomeStore.getState();
    const currentCategory = store.homeCategoryFilter;

    if (store.isLoadingMore || !store.hasMoreContentCache[currentCategory]) return;

    const { scrollTop, scrollHeight, clientHeight } = homeGrid;
    // 스크롤이 하단에서 200px 이내에 오면 추가 로드
    if (scrollTop + clientHeight >= scrollHeight - 200) {
      loadHomeContent(0, true);
    }
  });
}

// ============================================
// 홈 그리드 렌더링
// ============================================

export function renderHomeGrid(append = false, newItems = null) {
  const container = document.getElementById('homeContentGrid');
  if (!container) return;

  const store = useHomeStore.getState();
  const currentType = store.homeCategoryFilter;
  const cached = store.publishedContentCache[currentType] || [];

  // 로딩 인디케이터 먼저 제거
  const loadingEl = container.querySelector('.load-more-spinner');
  if (loadingEl) loadingEl.remove();

  if (append && newItems) {
    // append 모드: 새 아이템 추가 (이미 타입별로 필터됨)
    if (newItems.length > 0) {
      const html = newItems.map(content => renderHomeCard(content)).join('');
      container.insertAdjacentHTML('beforeend', html);
    }
  } else {
    // 초기 로드: 전체 렌더링
    if (cached.length === 0) {
      container.innerHTML = '<div class="empty-state-section">콘텐츠가 없습니다</div>';
      return;
    }

    container.innerHTML = cached.map(content => renderHomeCard(content)).join('');
  }

  // 노출 추적을 위한 IntersectionObserver 연결
  const observer = getImpressionObserver();
  container.querySelectorAll('.home-card-wrapper[data-content-id]').forEach(card => {
    if (!observedCards.has(card.dataset.contentId)) {
      observer.observe(card);
    }
  });

  // 더 불러올 콘텐츠가 있으면 로딩 인디케이터 추가
  if (store.hasMoreContentCache[currentType]) {
    container.insertAdjacentHTML('beforeend', '<div class="load-more-spinner"><div class="spinner" style="width:24px;height:24px;"></div></div>');
  }
}

// ============================================
// 홈 카드 렌더링
// Note: innerHTML 사용 시 escapeHtml()로 모든 사용자 입력을 이스케이프
// ============================================

export function renderHomeCard(content) {
  const tags = content.tags || [];
  // 언필터 뱃지는 safetyFilter가 명시적으로 false일 때만 표시
  const showUnfilter = content.safetyFilter === false;
  const unfilterTag = showUnfilter ? '<span class="home-card-tag unfilter">언필터</span>' : '';
  const regularTags = tags.map(tag => `<span class="home-card-tag">#${escapeHtml(tag.replace(/^#/, ''))}</span>`).join('');
  const tagsHtml = unfilterTag + regularTags;

  const safeTitle = escapeHtml(content.title || '제목 없음');
  const safeDesc = escapeHtml(content.shortDesc || content.description || '');

  return `
    <div class="home-card-wrapper" data-content-id="${content.id}">
      <div class="home-card">
        <div class="home-card-inner">
          ${content.thumbUrl || content.imageUrl
            ? `<div class="img-loading-wrapper"><div class="spinner"></div><img class="home-card-image" src="${content.thumbUrl || content.imageUrl}" alt="${safeTitle}" onload="this.classList.add('loaded');this.parentElement&&this.parentElement.classList.add('loaded')" onerror="if(this.dataset.fallback!=='1'&&'${content.imageUrl}'){this.dataset.fallback='1';this.src='${content.imageUrl}';}"></div>`
            : `<div class="home-card-placeholder">👤</div>`
          }
          <div class="home-card-plays">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="white"><path d="M12 2C6.48 2 2 6.04 2 11c0 2.76 1.36 5.22 3.5 6.83V22l4.09-2.25c.75.15 1.54.25 2.41.25 5.52 0 10-4.04 10-9S17.52 2 12 2z"/></svg>
            <span>${formatPlayCount(content.chatCount)}</span>
          </div>
          <div class="home-card-overlay">
            <div class="home-card-title">${safeTitle}</div>
            <div class="home-card-desc">${safeDesc}</div>
            <div class="home-card-tags">${tagsHtml}</div>
          </div>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// 유사 콘텐츠 로드
// ============================================

export async function loadSimilarContent(contentId, limit = 6) {
  try {
    const store = useHomeStore.getState();
    const db = store.getDb();

    // 1. 소스 콘텐츠 조회 (캐시에서 먼저 찾기)
    let sourceContent = store.publishedContentCache?.all?.find(c => c.id === contentId);

    if (!sourceContent) {
      const doc = await db.collection('published').doc(contentId).get();
      if (!doc.exists) return [];
      sourceContent = { id: doc.id, ...doc.data() };
    }

    // 원본 태그 (쿼리용)
    const originalTags = (sourceContent.tags || []).filter(t => t && t.trim().length > 0);
    if (originalTags.length === 0) return [];

    // 정규화된 태그 (비교용)
    const normalizedSourceTags = originalTags.map(t => t.replace(/^#/, '').trim().toLowerCase());

    // 2. 태그 기반 쿼리 (최대 10개 태그, 누적 점수순) - 원본 태그로 쿼리
    const queryTags = originalTags.slice(0, 10);
    const snap = await db.collection('published')
      .where('status', '==', 'public')
      .where('tags', 'array-contains-any', queryTags)
      .orderBy('scoreAllTime', 'desc')
      .limit(30)
      .get();

    if (snap.empty) return [];

    // 3. Jaccard 유사도 계산 + 자기 자신 제외
    const candidates = snap.docs
      .filter(doc => doc.id !== contentId)
      .map(doc => {
        const data = doc.data();
        const docTags = (data.tags || [])
          .map(t => t.replace(/^#/, '').trim().toLowerCase())
          .filter(t => t.length > 0);

        const matchingTags = normalizedSourceTags.filter(t => docTags.includes(t));
        const intersection = matchingTags.length;
        const union = new Set([...normalizedSourceTags, ...docTags]).size;
        const similarity = union > 0 ? intersection / union : 0;

        return {
          id: doc.id,
          title: data.title || '제목 없음',
          shortDesc: data.shortDesc || '',
          imageUrl: data.imageUrl || null,
          thumbUrl: data.thumbUrl || null,
          chatCount: data.chatCount || 0,
          likeCount: data.likeCount || 0,
          scoreAllTime: data.scoreAllTime || 0,
          authorNickname: data.authorNickname || data.creatorNickname || '익명',
          authorId: data.authorId || data.creatorId || null,
          tags: data.tags || [],
          similarity,
          matchingTags
        };
      });

    // 4. 유사도 * 누적점수로 정렬
    candidates.sort((a, b) => {
      const scoreA = a.similarity * 0.6 + Math.min(a.scoreAllTime / 1000, 0.4);
      const scoreB = b.similarity * 0.6 + Math.min(b.scoreAllTime / 1000, 0.4);
      return scoreB - scoreA;
    });

    return candidates.slice(0, limit);

  } catch (e) {
    console.error('[Home] 유사 콘텐츠 로드 오류:', e);
    return [];
  }
}

// ============================================
// 필수 window 노출 (다른 모듈에서 호출)
// ============================================
// navigation.js에서 탭 전환 시 호출
window.loadHomeContent = loadHomeContent;
